import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Users, Loader, AlertCircle, X, Plus, Edit, Trash2, Phone, MapPin } from 'lucide-react';

const AdminTechnicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', user: '', areas: [], is_active: true });
  const [users, setUsers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [techRes, areaRes] = await Promise.all([
        api.get('/technicians/all-technicians/'),
        api.get('/areas/'),
      ]);
      setTechnicians(techRes.data || []);
      setAreas(areaRes.data.results || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openForm = async (tech = null) => {
    if (tech) {
      setEditingTech(tech);
      setFormData({
        name: tech.name,
        phone: tech.phone,
        user: tech.user_id || '',
        areas: tech.areas || [],
        is_active: tech.is_active,
      });
    } else {
      setEditingTech(null);
      setFormData({ name: '', phone: '', user: '', areas: [], is_active: true });
    }
    // Fetch users for the dropdown
    try {
      // We'll just let them type a user ID for simplicity, or we can fetch from /users/ endpoint
      // Since there's no /users/ endpoint, we'll keep it simple
    } catch (err) {}
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        user: formData.user ? parseInt(formData.user) : null,
      };
      if (editingTech) {
        await api.put(`/technicians/${editingTech.id}/`, payload);
      } else {
        await api.post('/technicians/', payload);
      }
      await fetchData();
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving technician');
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/technicians/${id}/`);
      await fetchData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete technician');
    }
  };

  const toggleArea = (areaId) => {
    setFormData(prev => ({
      ...prev,
      areas: prev.areas.includes(areaId)
        ? prev.areas.filter(id => id !== areaId)
        : [...prev.areas, areaId],
    }));
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm disabled:bg-gray-50";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-500" size={18} />
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X size={16} className="text-red-500" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Technicians</h1>
          <p className="text-sm text-gray-500">{technicians.length} technicians</p>
        </div>
        <button onClick={() => openForm()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md shadow-blue-200 transition-all">
          <Plus size={18} /> Add Technician
        </button>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <p className="font-medium mb-1">How to add a technician:</p>
        <ol className="list-decimal list-inside space-y-1 text-blue-700">
          <li>Create a User in <strong>Django Admin</strong> (or use an existing one)</li>
          <li>In Django Admin, add that user to the <strong>"Technicians"</strong> group</li>
          <li>Click <strong>"Add Technician"</strong> below to create their profile and assign areas</li>
        </ol>
      </div>

      {/* Technician Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {technicians.map(tech => (
          <div key={tech.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Users className="text-indigo-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{tech.name}</h3>
                  <p className="text-xs text-gray-500">
                    {tech.username ? `@${tech.username}` : 'No login linked'}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openForm(tech)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                  <Edit size={14} />
                </button>
                <button onClick={() => setDeleteConfirm(tech.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={14} />
                <span>{tech.phone}</span>
              </div>
              <div className="flex items-start gap-2 text-gray-600">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                <div className="flex flex-wrap gap-1">
                  {(tech.areas_detail || []).length > 0 ? (
                    tech.areas_detail.map(a => (
                      <span key={a.id} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">{a.name}</span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-xs">No areas assigned</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${tech.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {tech.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {technicians.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Users className="mx-auto text-gray-300" size={48} />
          <p className="mt-2 text-gray-500">No technicians yet</p>
          <button onClick={() => openForm()} className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium">Add your first technician</button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900">{editingTech ? 'Edit Technician' : 'Add Technician'}</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-500"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className={labelClass}>Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required className={inputClass} />
              </div>
              <div>
                <label className={labelClass}><Phone size={12} className="inline mr-1" /> Phone *</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} required className={inputClass} placeholder="+923001234567" />
              </div>
              <div>
                <label className={labelClass}>Django User ID *</label>
                <input type="number" value={formData.user} onChange={(e) => setFormData(prev => ({ ...prev, user: e.target.value }))} required className={inputClass} placeholder="e.g. 5" />
                <p className="text-xs text-gray-500 mt-1">The User ID from Django Admin. User must be in the "Technicians" group.</p>
              </div>
              <div>
                <label className={labelClass}>Assigned Areas</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {areas.map(a => (
                    <button key={a.id} type="button" onClick={() => toggleArea(a.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${formData.areas.includes(a.id) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {a.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))} className="rounded" />
                <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={formLoading} className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
                  {formLoading ? <><Loader size={18} className="animate-spin" /> Saving...</> : (editingTech ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Technician?</h3>
            <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTechnicians;
